import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, MessageCircle, Send, ExternalLink, Smile } from "lucide-react";
import { api } from "@/lib/api";

const DISCORD_URL = "https://discord.gg/nQKVtqFMsv";

const COLORS = [
  { v: "orange", label: "Orange", bar: "#F97316", bg: "rgba(249, 115, 22, 0.12)", border: "rgba(249, 115, 22, 0.35)" },
  { v: "emerald", label: "Émeraude", bar: "#10B981", bg: "rgba(16, 185, 129, 0.12)", border: "rgba(16, 185, 129, 0.35)" },
  { v: "rose", label: "Rose", bar: "#F43F5E", bg: "rgba(244, 63, 94, 0.12)", border: "rgba(244, 63, 94, 0.35)" },
  { v: "sky", label: "Ciel", bar: "#38BDF8", bg: "rgba(56, 189, 248, 0.12)", border: "rgba(56, 189, 248, 0.35)" },
  { v: "amber", label: "Ambre", bar: "#F59E0B", bg: "rgba(245, 158, 11, 0.12)", border: "rgba(245, 158, 11, 0.35)" },
  { v: "violet", label: "Violet", bar: "#A78BFA", bg: "rgba(167, 139, 250, 0.12)", border: "rgba(167, 139, 250, 0.35)" },
];

const EMOJIS = ["💛", "🌱", "🌈", "✨", "🫶", "💪", "🕊️", "☀️", "🌙", "🔥"];

function colorStyles(v) {
  const c = COLORS.find((x) => x.v === v);
  if (!c) return {};
  return {
    background: `linear-gradient(180deg, ${c.bg}, rgba(15, 23, 42, 0.9))`,
    borderColor: c.border,
    boxShadow: `inset 4px 0 0 0 ${c.bar}`,
  };
}

export default function CommunitySection() {
  const [messages, setMessages] = useState([]);
  const [author, setAuthor] = useState("");
  const [message, setMessage] = useState("");
  const [color, setColor] = useState("orange");
  const [emoji, setEmoji] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/encouragements");
      setMessages(r.data || []);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() || message.trim().length < 2) {
      toast.error("Ajoute un petit mot, même court.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/encouragements", {
        author,
        message,
        color,
        emoji: emoji || null,
      });
      toast.success("Merci pour ton message, il va faire du bien.");
      setMessage("");
      setEmoji("");
      await load();
    } catch {
      toast.error("Impossible d'envoyer pour l'instant.");
    } finally {
      setSubmitting(false);
    }
  };

  const previewColor = useMemo(() => COLORS.find((c) => c.v === color), [color]);

  return (
    <section id="communaute" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="em-eyebrow">Communauté</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Un mur d'encouragements, un Discord accueillant.
            </h2>
            <p className="mt-4 text-slate-300">
              Laisse un mot positif pour quelqu'un qui en aura besoin ce soir. Et viens
              discuter en direct sur notre serveur.
            </p>

            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="discord-button"
              className="mt-6 inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold px-6 py-3 rounded-full shadow-lg shadow-indigo-500/20"
            >
              <MessageCircle className="w-4 h-4" />
              Rejoindre le Discord
              <ExternalLink className="w-3.5 h-3.5 opacity-80" />
            </a>

            <form onSubmit={submit} className="mt-10 em-card p-6" data-testid="encouragement-form">
              <h3 className="text-white font-semibold text-lg font-[Outfit]">
                Dépose un mot bienveillant
              </h3>
              <div className="mt-4 grid gap-3">
                <input
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Pseudo (facultatif)"
                  data-testid="encouragement-author"
                  className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={40}
                />
                <textarea
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ton mot d'encouragement pour quelqu'un qui lira ça demain…"
                  data-testid="encouragement-message"
                  className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={400}
                  required
                />

                <div>
                  <p className="text-xs em-eyebrow">Couleur</p>
                  <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Couleur du mot">
                    {COLORS.map((c) => (
                      <button
                        key={c.v}
                        type="button"
                        role="radio"
                        aria-checked={color === c.v}
                        onClick={() => setColor(c.v)}
                        data-testid={`enc-color-${c.v}`}
                        className={`w-8 h-8 rounded-full border-2 transition ${
                          color === c.v ? "ring-2 ring-white/50 scale-110" : "border-transparent opacity-70 hover:opacity-100"
                        }`}
                        style={{ background: c.bar, borderColor: color === c.v ? "#F8FAFC" : "transparent" }}
                        title={c.label}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs em-eyebrow flex items-center gap-2">
                    <Smile className="w-3.5 h-3.5" /> Emoji (facultatif)
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Emoji du mot">
                    <button
                      type="button"
                      onClick={() => setEmoji("")}
                      data-testid="enc-emoji-none"
                      className={`w-9 h-9 rounded-full text-xs text-slate-300 ${
                        emoji === "" ? "bg-white/10 ring-2 ring-white/40" : "bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      ✕
                    </button>
                    {EMOJIS.map((e) => (
                      <button
                        key={e}
                        type="button"
                        role="radio"
                        aria-checked={emoji === e}
                        onClick={() => setEmoji(e)}
                        data-testid={`enc-emoji-${e}`}
                        className={`w-9 h-9 rounded-full text-lg transition ${
                          emoji === e ? "bg-white/15 ring-2 ring-white/50" : "bg-white/5 hover:bg-white/10"
                        }`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Live preview */}
                <div
                  className="rounded-2xl p-4 border"
                  data-testid="encouragement-preview"
                  style={colorStyles(color)}
                >
                  <p className="text-sm text-slate-100">
                    {emoji && <span className="mr-2">{emoji}</span>}
                    « {message || "Ton message apparaîtra ici…"} »
                  </p>
                  <p className="mt-2 text-xs text-slate-400">
                    Aperçu · {previewColor?.label}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="encouragement-submit"
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold px-5 py-3 rounded-full"
                >
                  <Send className="w-4 h-4" />
                  {submitting ? "Envoi…" : "Poster mon mot"}
                </button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-7">
            {messages.length === 0 ? (
              <div className="em-card p-8 text-slate-400 italic" data-testid="encouragement-wall-empty">
                Sois la première personne à écrire un mot ici. Ça peut sauver la soirée de quelqu'un.
              </div>
            ) : (
              <div className="columns-1 sm:columns-2 gap-4 [column-fill:_balance]" data-testid="encouragement-wall">
                <AnimatePresence initial={false}>
                  {messages.map((m) => (
                    <motion.article
                      key={m.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="p-5 mb-4 break-inside-avoid rounded-2xl border transition hover:-translate-y-0.5"
                      style={colorStyles(m.color)}
                      data-testid={`encouragement-${m.id}`}
                    >
                      <p className="text-slate-100 leading-relaxed">
                        {m.emoji && <span className="mr-2 text-xl align-middle">{m.emoji}</span>}
                        « {m.message} »
                      </p>
                      <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                        <Heart className="w-3.5 h-3.5 text-rose-400" />
                        <span>{m.author}</span>
                      </div>
                    </motion.article>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
